/**
 * Goals — defaults, persistence and pace math.
 *
 * Requirements are set per title group, not per person: a firm decides
 * "partners bill 1,700 a year", not a separate number for each individual.
 *
 * There is no backend goal store, so the overrides live in localStorage. The
 * consumers only ever see `GoalsByGroup`, so swapping this for a real endpoint
 * later is a change to load/save, not to the tabs.
 */

import { countWeekdays } from "@/shared/utils/workdays";
import type { GoalsByGroup, PaceStatus } from "../types/reports";
import {
	DEFAULT_ANNUAL_HOURS_BY_GROUP,
	normalizeProfType,
	type ProfTitleGroup,
} from "./profTitles";

const STORAGE_KEY = "hg.admin.reports.goals.v2";

/** Fallback for a group with no configured or default requirement. */
export const DEFAULT_ANNUAL_HOURS = 0;

// ─────────────────────────────────────────────
// Persistence
// ─────────────────────────────────────────────

export function loadGoals(): GoalsByGroup {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return {};
		const parsed = JSON.parse(raw);
		return typeof parsed === "object" && parsed !== null ? parsed : {};
	} catch {
		// Private windows and cleared site data both land here. An empty map is
		// correct: every group falls back to its default.
		return {};
	}
}

export function saveGoals(goals: GoalsByGroup): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(goals));
	} catch {
		// Storage unavailable or full — the session keeps working off in-memory
		// state, the values just do not survive a reload.
	}
}

/** Fill in every group the firm has not overridden, so lookups never miss. */
export function withDefaults(stored: GoalsByGroup): GoalsByGroup {
	const out: GoalsByGroup = {};
	for (const group of Object.keys(
		DEFAULT_ANNUAL_HOURS_BY_GROUP,
	) as ProfTitleGroup[]) {
		out[group] = stored[group] ?? DEFAULT_ANNUAL_HOURS_BY_GROUP[group];
	}
	return out;
}

export function setGoal(
	goals: GoalsByGroup,
	group: ProfTitleGroup,
	annualHours: number,
): GoalsByGroup {
	return { ...goals, [group]: annualHours };
}

/** True when the group still carries its shipped default. */
export function isDefaultGoal(
	stored: GoalsByGroup,
	group: ProfTitleGroup,
): boolean {
	return stored[group] === undefined;
}

/** Annual requirement for a raw `prof_type`, via its title group. */
export function annualHoursForProfType(
	goals: GoalsByGroup,
	profType: string | null | undefined,
): number {
	const group = normalizeProfType(profType);
	return goals[group] ?? DEFAULT_ANNUAL_HOURS_BY_GROUP[group];
}

// ─────────────────────────────────────────────
// Pace math
// ─────────────────────────────────────────────

/**
 * Business days elapsed and total for a period.
 *
 * `asOf` is clamped into the period so a past month reads as fully elapsed
 * rather than over-elapsed.
 */
export function periodProgress(
	from: Date,
	to: Date,
	asOf: Date,
): { elapsed: number; total: number; fraction: number } {
	const total = countWeekdays(from, to) || 1;
	const clamped = asOf < from ? from : asOf > to ? to : asOf;
	const elapsed = countWeekdays(from, clamped);
	return { elapsed, total, fraction: Math.min(elapsed / total, 1) };
}

/** Annual requirement scaled to the share of the year the period covers. */
export function targetForPeriod(
	annualHours: number,
	periodWorkdays: number,
	yearWorkdays: number,
): number {
	if (yearWorkdays <= 0) return 0;
	return (annualHours * periodWorkdays) / yearWorkdays;
}

/**
 * Actual hours as a percentage of what should have been entered by now.
 *
 * 100 is exactly on pace. Returns null when there is no target to measure
 * against, and null rather than Infinity when nothing is expected yet.
 */
export function pacePct(
	hours: number,
	target: number | null,
	elapsedFraction: number,
): number | null {
	if (target === null || target <= 0) return null;
	const expectedByNow = target * elapsedFraction;
	if (expectedByNow <= 0) return null;
	return Math.round((hours / expectedByNow) * 100);
}

/** Hours ahead of or behind where the timekeeper should be right now. */
export function variance(
	hours: number,
	target: number | null,
	elapsedFraction: number,
): number | null {
	if (target === null) return null;
	return hours - target * elapsedFraction;
}

/** Within 5% either way reads as on pace — tighter than that is noise. */
const ON_PACE_TOLERANCE = 5;

export function paceStatus(pct: number | null): PaceStatus {
	if (pct === null) return "no-goal";
	if (pct >= 100 + ON_PACE_TOLERANCE) return "ahead";
	if (pct <= 100 - ON_PACE_TOLERANCE) return "behind";
	return "on";
}
